import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  Dialog,
  DialogSurface,
  DialogBody,
  tokens,
  Toolbar,
  Button,
  makeStyles,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  Spinner,
} from "@fluentui/react-components";
import { Dismiss20Regular, Dismiss24Regular, SendRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import RichTextEditor from "../../../shared/components/RichTextEditor";
import Files from "../../files/components/Files";
import { GenericComboBox } from "../../../shared/components/GenericComboBox";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { useAuth } from "../../../provider/authProvider";
import { readMailboxes } from "../../mailboxes/services/mailboxApi";
import { readUsers } from "../../users/services/userApi";

import { useCreateTicketUpdate } from "../hooks/useTicketUpdateMutations";

const useStyles = makeStyles({
  container: {
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "0",
    backgroundColor: "white",
    position: "relative",
  },
  emailFields: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    borderBottom: "1px solid #e0e0e0",
  },
  field: {
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    gap: "8px",
  },
  fieldLabel: {
    minWidth: "80px",
    fontWeight: "500",
  },
});

const TicketMailEditor = ({
  ticket,
  selectedMailboxId,
  mailProperties,
  setMailProperties,
  selectedLanguage,
  isOpen,
  onOpenChange,
  //value = "",
  onChange,
  onLanguageChange,
  //toAddress = "",
  //ccAddress = "",
  //subject = `[#${ticket?.type}-${ticket?.id}] ${ticket?.title}`,
  //onToChange,
  //onCcChange,
  onMailboxChange,
  onClose,
}) => {
  const editorRef = useRef(null);
  const { t } = useTranslation();
  const styles = useStyles();
  const { user: currentUser } = useAuth();
  const attachmentsRef = useRef(null);
  const [toInputQuery, setToInputQuery] = useState("");
  const [ccInputQuery, setCcInputQuery] = useState("");
  const [emailList, setEmailList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [selectedFromMailbox, setSelectedFromMailbox] = useState(selectedMailboxId || null);
  const [canSend, setCanSend] = useState(false);
  //const [mailSubject, setMailSubject] = useState(subject);

  const { data: mailboxData } = useItemQuery(["mailbox"], readMailboxes, true, "TicketMailEditor");
  const { data: usersData } = useItemQuery(["users"], readUsers, true, "TicketMailEditor");

  const { mutate: createTicketUpdate, isPending: isCreatingTicketUpdate } = useCreateTicketUpdate(
    ["TicketUpdates", { ticket_id: ticket?.id }],
    async (data) => {
      // If success - cleanup and close dialog
      console.log("Success callback called");
      attachmentsRef.current?.clearPending();
      editorRef.current?.clear();
      setMailProperties({});
      onOpenChange?.(null, { open: false });
    },
    null,
    true,
  );

  // Monitor isCreatingTicketUpdate changes
  useEffect(() => {
    console.log("isCreatingTicketUpdate changed:", isCreatingTicketUpdate);
  }, [isCreatingTicketUpdate]);

  const handleMailboxSelect = (mailboxId) => {
    setSelectedFromMailbox(mailboxId);
    onMailboxChange?.(mailboxId);
  };

  // Calculate if send button should be enabled
  useEffect(() => {
    const canSendEmail =
      selectedFromMailbox !== null &&
      selectedFromMailbox !== undefined &&
      !!mailProperties?.to?.trim() &&
      !!mailProperties?.subject?.trim() &&
      !!mailProperties?.body?.trim();
    setCanSend(canSendEmail);
  }, [
    isOpen,
    selectedFromMailbox,
    mailProperties?.to,
    mailProperties?.cc,
    mailProperties?.subject,
    mailProperties?.body,
  ]);

  // Initialize email list from toAddress prop
  useEffect(() => {
    const emails = mailProperties?.to
      ? mailProperties?.to
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean)
      : [];
    setEmailList(emails);
  }, [mailProperties?.to]);

  // Initialize CC list from ccAddress prop
  useEffect(() => {
    const emails = mailProperties?.cc
      ? mailProperties?.cc
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean)
      : [];
    setCcList(emails);
  }, [mailProperties?.cc]);

  // Set selectedMailbox to match mailProperties.from if present
  useEffect(() => {
    if (mailboxData && mailProperties?.from) {
      // Extract email from "Name <email@domain.com>" format or use as-is
      const emailMatch = mailProperties.from.match(/<(.+?)>/);
      const fromEmail = emailMatch ? emailMatch[1] : mailProperties.from;

      // Find matching mailbox
      const matchedMailbox = mailboxData.find((m) => m.email_address === fromEmail);
      if (matchedMailbox) {
        setSelectedFromMailbox(matchedMailbox.id);
        onMailboxChange?.(matchedMailbox.id);
      }
    }
  }, [mailboxData, mailProperties?.from, onMailboxChange]);

  // Auto-select first mailbox when data loads
  useEffect(() => {
    if (mailboxData && mailboxData.length > 0 && !selectedMailboxId && !mailProperties?.from) {
      const firstMailboxId = mailboxData[0].id;
      setSelectedFromMailbox(firstMailboxId);
      onMailboxChange?.(firstMailboxId);
    }
  }, [mailboxData, selectedMailboxId, mailProperties?.from, onMailboxChange]);

  const addEmailTag = (email) => {
    const trimmedEmail = email.trim();
    if (trimmedEmail && !emailList.includes(trimmedEmail)) {
      const newEmails = [...emailList, trimmedEmail];
      setEmailList(newEmails);
      setMailProperties({ ...mailProperties, to: newEmails.join(", ") });
      setToInputQuery("");
    }
  };

  const removeEmailTag = (emailToRemove) => {
    console.log("Removing email:", emailToRemove);
    const newEmails = emailList.filter((email) => email !== emailToRemove);
    setEmailList(newEmails);
    setMailProperties({ ...mailProperties, to: newEmails.join(", ") });
  };

  const handleToInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmailTag(toInputQuery);
    }
  };

  const addCcTag = (email) => {
    const trimmedEmail = email.trim();
    if (trimmedEmail && !ccList.includes(trimmedEmail)) {
      const newEmails = [...ccList, trimmedEmail];
      setCcList(newEmails);
      setMailProperties({ ...mailProperties, cc: newEmails.join(", ") });
      setCcInputQuery("");
    }
  };

  const removeCcTag = (emailToRemove) => {
    const newEmails = ccList.filter((email) => email !== emailToRemove);
    setCcList(newEmails);
    setMailProperties({ ...mailProperties, cc: newEmails.join(", ") });
  };

  const handleCcInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCcTag(ccInputQuery);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogSurface
        id="edit-ticket-mail-dialog"
        style={{
          border: `1px solid ${tokens.colorNeutralStroke1}`,
          borderRadius: "4px",
          minWidth: "600px",
          maxWidth: "50%",
          padding: "0",
          margin: "auto",
          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)",
        }}
      >
        <DialogBody
          id="edit-ticket-mail-dialog-body"
          style={{ width: "100%", display: "flex", flexDirection: "column", padding: "20px", margin: "0" }}
        >
          <div className={styles.emailFields}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>{t("generic.from", { defaultValue: "From" })} *</label>
              <div style={{ flex: 1 }}>
                <GenericComboBox
                  items={mailboxData || []}
                  value={selectedFromMailbox}
                  valueKey="id"
                  displayKey="email_address"
                  placeholder={t("generic.selectMailbox", { defaultValue: "Select mailbox" })}
                  onOptionSelect={handleMailboxSelect}
                  disabled={mailboxData && mailboxData.length <= 1}
                />
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>{t("generic.to", { defaultValue: "To" })} *</label>
              <div style={{ flex: 1 }}>
                <TagPicker
                  size="medium"
                  onOptionSelect={(e, data) => {
                    if (data.value) {
                      addEmailTag(data.value);
                    }
                  }}
                  selectedOptions={emailList}
                >
                  <TagPickerControl>
                    <TagPickerGroup size="small">
                      {emailList.map((email) => (
                        <div
                          key={email}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "2px 8px",
                            backgroundColor: "#f0f0f0",
                            borderRadius: "4px",
                            marginRight: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <span>{email}</span>
                          <button
                            onClick={() => removeEmailTag(email)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Remove"
                          >
                            <Dismiss20Regular style={{ fontSize: "16px" }} />
                          </button>
                        </div>
                      ))}
                    </TagPickerGroup>
                    <TagPickerInput
                      size="small"
                      value={toInputQuery}
                      onChange={(e) => setToInputQuery(e.target.value)}
                      onKeyDown={handleToInputKeyDown}
                      onBlur={() => {
                        if (toInputQuery.trim()) {
                          addEmailTag(toInputQuery);
                        }
                      }}
                      placeholder={t("generic.recipientEmail", { defaultValue: "recipient@example.com" })}
                    />
                  </TagPickerControl>
                  <TagPickerList>
                    {usersData &&
                      usersData
                        .filter((user) => {
                          if (!toInputQuery) return false;
                          const query = toInputQuery.toLowerCase();
                          return user.email?.toLowerCase().includes(query) || user.name?.toLowerCase().includes(query);
                        })
                        .slice(0, 5)
                        .map((user) => (
                          <TagPickerOption key={user.id} value={user.email}>
                            {user.name} ({user.email})
                          </TagPickerOption>
                        ))}
                    {toInputQuery && (
                      <TagPickerOption value={toInputQuery}>
                        {t("generic.addEmail", { defaultValue: "Add" })}: {toInputQuery}
                      </TagPickerOption>
                    )}
                  </TagPickerList>
                </TagPicker>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>{t("generic.cc", { defaultValue: "CC" })}</label>
              <div style={{ flex: 1 }}>
                <TagPicker
                  size="medium"
                  onOptionSelect={(e, data) => {
                    if (data.value) {
                      addCcTag(data.value);
                    }
                  }}
                  selectedOptions={ccList}
                >
                  <TagPickerControl>
                    <TagPickerGroup>
                      {ccList.map((email) => (
                        <div
                          key={email}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "2px 8px",
                            backgroundColor: "#f0f0f0",
                            borderRadius: "4px",
                            marginRight: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <span>{email}</span>
                          <button
                            onClick={() => removeCcTag(email)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Remove"
                          >
                            <Dismiss20Regular style={{ fontSize: "16px" }} />
                          </button>
                        </div>
                      ))}
                    </TagPickerGroup>
                    <TagPickerInput
                      size="small"
                      value={ccInputQuery}
                      onChange={(e) => setCcInputQuery(e.target.value)}
                      onKeyDown={handleCcInputKeyDown}
                      onBlur={() => {
                        if (ccInputQuery.trim()) {
                          addCcTag(ccInputQuery);
                        }
                      }}
                      placeholder={t("generic.recipientEmail", { defaultValue: "recipient@example.com" })}
                    />
                  </TagPickerControl>
                  <TagPickerList>
                    {usersData &&
                      usersData
                        .filter((user) => {
                          if (!ccInputQuery) return false;
                          const query = ccInputQuery.toLowerCase();
                          return user.email?.toLowerCase().includes(query) || user.name?.toLowerCase().includes(query);
                        })
                        .slice(0, 5)
                        .map((user) => (
                          <TagPickerOption key={user.id} value={user.email}>
                            {user.name} ({user.email})
                          </TagPickerOption>
                        ))}
                    {ccInputQuery && (
                      <TagPickerOption value={ccInputQuery}>
                        {t("generic.addEmail", { defaultValue: "Add" })}: {ccInputQuery}
                      </TagPickerOption>
                    )}
                  </TagPickerList>
                </TagPicker>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", minWidth: "80px" }}>
                {t("generic.subject", { defaultValue: "Subject" })} *
              </label>
              <Input
                style={{ flex: 1 }}
                size="medium"
                value={mailProperties?.subject}
                onChange={(e, data) => setMailProperties({ ...mailProperties, subject: data })}
                placeholder={t("generic.emailSubject", { defaultValue: "Enter subject" })}
              />
            </div>
          </div>
          <div
            style={{
              border: `1px solid ${tokens.colorNeutralStroke1}`,
              borderRadius: "4px",
              width: "100%",
              display: "flex",
              padding: "0",
              margin: "0",
            }}
          >
            <RichTextEditor
              ref={editorRef}
              //value={value}
              value={mailProperties?.body}
              onChange={(content) => setMailProperties({ ...mailProperties, body: content })}
              selectedLanguage={selectedLanguage}
              onLanguageChange={onLanguageChange}
              canSend={canSend}
            />
          </div>
          <div id="attachments-section" style={{ display: "flex" }}>
            <Files ref={attachmentsRef} style={{ flex: 1 }} entity_type="ticket_update" entity_id={null} editable />
            <Toolbar style={{ flex: "0 0 auto", marginLeft: "auto" }}>
              <Button
                appearance="secondary"
                onClick={() => {
                  setMailProperties({});
                  attachmentsRef.current?.clearPending();
                  editorRef.current?.clear();
                  onOpenChange?.(null, { open: false });
                }}
                style={{ marginRight: 8 }}
              >
                {t("generic.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button
                appearance="primary"
                disabled={!canSend || isCreatingTicketUpdate}
                icon={isCreatingTicketUpdate ? <Spinner size="tiny" /> : <SendRegular />}
                onClick={() => {
                  console.log("Button clicked, isCreatingTicketUpdate:", isCreatingTicketUpdate);
                  const pendingAttachments = attachmentsRef.current?.getPendingAttachments() || [];
                  console.log("About to call createTicketUpdate (non-blocking)");
                  createTicketUpdate({
                    ticket_id: ticket?.id,
                    author_id: currentUser?.id,
                    update_type: "outgoing_email",
                    content: editorRef.current?.getContent() || "",
                    language: editorRef.current?.getLanguage() || null,
                    mailbox_id: selectedFromMailbox,
                    from_address: mailboxData?.find((mb) => mb.id === selectedFromMailbox)?.email_address || "",
                    to_address: emailList.join(", "),
                    cc_address: ccList.join(", "),
                    subject: mailProperties?.subject,
                    attachments: pendingAttachments,
                  });
                  console.log("createTicketUpdate called, handler returning immediately");
                }}
              />
            </Toolbar>
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TicketMailEditor;
